// src/components/ServerDataExtraction/ExtractionHistory.jsx
import React from 'react';
import LoadingSkeleton from '../Shared/LoadingSkeleton'; // Import Skeleton

// Added isLoading and error props
const ExtractionHistory = ({ history, isLoading, error }) => {
    if (isLoading) {
        return (
            <div className="mb-8 transform-gpu hover:scale-[1.005] transition-medium">
                <h3 className="font-bold text-lg mb-5 flex items-center group">
                    {/* ... header ... */}
                     <div className="w-10 h-10 rounded-lg bg-accent-purple/20 flex items-center justify-center mr-2 transform-gpu group-hover:rotate-12 transition-medium"> <i className="fas fa-history text-accent-purple"></i> </div>
                    Data Extraction History
                     <i className="fas fa-spinner fa-spin text-xs ml-2 text-gray-400"></i>
                </h3>
                <div className="bg-dark-600 rounded-2xl p-6 hover:shadow-md transition-medium border border-dark-400 hover:border-accent-purple/30">
                    <LoadingSkeleton count={5} />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mb-8 transform-gpu hover:scale-[1.005] transition-medium">
                <h3 className="font-bold text-lg mb-5 flex items-center group">
                     {/* ... header ... */}
                      <div className="w-10 h-10 rounded-lg bg-accent-purple/20 flex items-center justify-center mr-2 transform-gpu group-hover:rotate-12 transition-medium"> <i className="fas fa-history text-accent-purple"></i> </div>
                    Data Extraction History
                </h3>
                <div className="bg-dark-600 rounded-2xl p-6 text-center text-accent-red border border-accent-red/30">
                    <i className="fas fa-exclamation-triangle mr-2"></i> Error loading history: {error}
                </div>
            </div>
        );
    }

    if (!history || history.length === 0) {
        return (
            <div className="mb-8 transform-gpu hover:scale-[1.005] transition-medium">
                <h3 className="font-bold text-lg mb-5 flex items-center group">
                     {/* ... header ... */}
                     <div className="w-10 h-10 rounded-lg bg-accent-purple/20 flex items-center justify-center mr-2 transform-gpu group-hover:rotate-12 transition-medium"> <i className="fas fa-history text-accent-purple"></i> </div>
                    Data Extraction History
                </h3>
                <div className="bg-dark-600 rounded-2xl p-6 text-center text-gray-400 hover:text-gray-300 transition-medium border border-dark-400 hover:border-accent-purple/30">
                    No extraction history available
                </div>
            </div>
        );
    }

    return (
        <div className="mb-8 transform-gpu hover:scale-[1.005] transition-medium">
            <h3 className="font-bold text-lg mb-5 flex items-center group">
                 {/* ... header ... */}
                 <div className="w-10 h-10 rounded-lg bg-accent-purple/20 flex items-center justify-center mr-2 transform-gpu group-hover:rotate-12 transition-medium"> <i className="fas fa-history text-accent-purple"></i> </div>
                Data Extraction History
            </h3>

            <div className="bg-dark-600 rounded-2xl overflow-hidden hover:shadow-md transition-medium border border-dark-400 hover:border-accent-purple/30">
                <div className="overflow-x-auto">
                     <table className="w-full text-sm min-w-[600px]"> {/* Added min-width */}
                        <thead className="bg-dark-500">
                            <tr>
                                <th className="py-3 px-4 text-left font-medium text-gray-300">Server</th>
                                <th className="py-3 px-4 text-left font-medium text-gray-300">Extracted Data</th>
                                <th className="py-3 px-4 text-left font-medium text-gray-300">Date</th>
                                <th className="py-3 px-4 text-left font-medium text-gray-300">Status</th>
                                <th className="py-3 px-4 text-left font-medium text-gray-300 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-dark-400">
                            {history.map((item, index) => (
                                <tr key={index} className="hover:bg-dark-500 transition cursor-pointer">
                                    <td className="py-3 px-4">
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 rounded-lg bg-dark-400 flex items-center justify-center mr-3">
                                                <i className="fas fa-server text-xs"></i>
                                            </div>
                                            <div>
                                                <p className="font-medium truncate max-w-[150px]">{item.server}</p> {/* Truncate */}
                                                <p className="text-xs text-gray-400">{item.ip}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="py-3 px-4">
                                        <div className="flex flex-wrap gap-1">
                                            {item.extractedData?.includes('info') && (<span className="px-1.5 py-0.5 bg-accent-blue bg-opacity-20 text-accent-blue rounded text-xs">Info</span>)}
                                            {item.extractedData?.includes('players') && (<span className="px-1.5 py-0.5 bg-accent-green bg-opacity-20 text-accent-green rounded text-xs">Players</span>)}
                                            {item.extractedData?.includes('plugins') && (<span className="px-1.5 py-0.5 bg-accent-purple bg-opacity-20 text-accent-purple rounded text-xs">Plugins</span>)}
                                            {item.extractedData?.includes('motd') && (<span className="px-1.5 py-0.5 bg-accent-yellow bg-opacity-20 text-accent-yellow rounded text-xs">MOTD</span>)}
                                            {item.extractedData?.includes('serverIcon') && (<span className="px-1.5 py-0.5 bg-accent-orange bg-opacity-20 text-accent-orange rounded text-xs">Icon</span>)}
                                        </div>
                                    </td>
                                    <td className="py-3 px-4 text-gray-400 whitespace-nowrap">
                                        {item.date} {/* Assuming pre-formatted */}
                                    </td>
                                    <td className="py-3 px-4">
                                        <span className={`px-2 py-1 rounded-lg text-xs ${item.status === 'complete' ? 'bg-accent-green bg-opacity-20 text-accent-green' : item.status === 'partial' ? 'bg-accent-yellow bg-opacity-20 text-accent-yellow' : 'bg-accent-red bg-opacity-20 text-accent-red'}`}>
                                            {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-right">
                                        <button className="text-gray-400 hover:text-white p-1"> <i className="fas fa-download"></i> </button>
                                        <button className="text-gray-400 hover:text-white p-1"> <i className="fas fa-eye"></i> </button>
                                        <button className="text-gray-400 hover:text-white p-1"> <i className="fas fa-ellipsis-v"></i> </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ExtractionHistory;